/**
 * RoleRouter Component
 *
 * Reads user role from Clerk and routes to the appropriate dashboard.
 * - student → StudentDashboard
 * - teacher → TeacherDashboard
 * - institution_admin → AdminDashboard
 * - super_admin → AdminDashboard (with extra powers)
 */

import { useUser, useAuth } from '@clerk/clerk-react';
import { getUserRole, type UserRole } from '../lib/clerk';
import { DashboardShell } from './DashboardShell';
import { StudentDashboard } from '../pages/StudentDashboard';
import { TeacherDashboard } from '../pages/TeacherDashboard';
import { AdminDashboard } from '../pages/AdminDashboard';

interface RoleRouterProps {
  // Optional: Force a specific role for testing
  forceRole?: UserRole;
}

export function RoleRouter({ forceRole }: RoleRouterProps) {
  const { user } = useUser();
  const { isLoaded } = useAuth();

  // Get user role from Clerk metadata
  const userRole: UserRole = forceRole ?? getUserRole(user);

  // Get organization name from unsafe metadata (for admin users)
  const orgName = (user?.unsafeMetadata as { organizationName?: string })?.organizationName ?? 'Your Organization';

  // Render appropriate dashboard based on role
  const renderDashboard = () => {
    switch (userRole) {
      case 'student':
        return (
          <DashboardShell role="student" title="Student Dashboard">
            <StudentDashboard />
          </DashboardShell>
        );

      case 'teacher':
        return (
          <DashboardShell role="teacher" title="Teacher Dashboard">
            <TeacherDashboard />
          </DashboardShell>
        );

      case 'institution_admin':
        return (
          <DashboardShell
            role="institution_admin"
            title="Admin Dashboard"
            organization={{
              name: orgName,
              tier: 'Professional',
            }}
          >
            <AdminDashboard isSuperAdmin={false} />
          </DashboardShell>
        );

      case 'super_admin':
        return (
          <DashboardShell
            role="super_admin"
            title="Super Admin Dashboard"
          >
            <AdminDashboard isSuperAdmin={true} />
          </DashboardShell>
        );

      default:
        // Fallback to student dashboard for unknown roles
        return (
          <DashboardShell role="student" title="Student Dashboard">
            <StudentDashboard />
          </DashboardShell>
        );
    }
  };

  // Loading state
  if (!isLoaded) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-secondary)',
        fontFamily: 'var(--font-family-mono)',
        fontSize: 'var(--font-size-sm)',
      }}>
        <span>Initializing...</span>
      </div>
    );
  }

  return <>{renderDashboard()}</>;
}
